// ThumbnailHelper.ts - 修正私有属性问题
import MimeUtil from '../../../common/util/MimeUtil';
import Matter from '../../../common/model/matter/Matter';
import DownloadToken from '../../../common/model/download/token/DownloadToken';

/**
 * 缩略图帮助器 - 专门用于网格视图显示图片缩略图
 */
export default class ThumbnailHelper {
  // 支持缩略图的图片格式 - 改为公共静态属性
  public static readonly THUMBNAIL_IMAGE_TYPES = [
    'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'
  ];

  // 文件类型图标映射 - 改为公共静态属性
  public static readonly FILE_TYPE_ICONS: Record<string, string> = {
    // 文档
    'pdf': '📄',
    'doc': '📝',
    'docx': '📝',
    'ppt': '📊',
    'pptx': '📊',
    'xls': '📈',
    'xlsx': '📈',
    'txt': '📃',
    'md': '📑',
    'rtf': '📄',
    
    // 压缩文件
    'zip': '📦',
    'rar': '📦',
    '7z': '📦',
    'tar': '📦',
    'gz': '📦',
    
    // 音视频
    'mp3': '🎵',
    'mp4': '🎬',
    'avi': '🎬',
    'mov': '🎬',
    'wav': '🎵',
    'flac': '🎵',
    'mkv': '🎬',
    
    // CAD和思维导图
    'dxf': '📐',
    'dwg': '📐',
    'xmind': '🧠',
    'mm': '🧠',
    'pos': '🧠',
    'eddx': '🧠',
    
    // 图片
    'jpg': '🖼️',
    'jpeg': '🖼️',
    'png': '🖼️',
    'gif': '🖼️',
    'bmp': '🖼️',
    'webp': '🖼️',
    'svg': '🖼️',
    
    // 代码文件
    'js': '📜',
    'ts': '📜',
    'html': '🌐',
    'css': '🎨',
    'java': '☕',
    'py': '🐍',
    'cpp': '⚙️',
    'c': '⚙️',
    'php': '🐘',
    
    // 默认图标
    '_default': '📁',
    '_folder': '📂'
  };

  /**
   * 检查文件是否支持显示缩略图（图片类型）
   */
  static isThumbnailSupported(fileName: string): boolean {
    const extension = MimeUtil.getExtensionWithoutDot(fileName);
    if (!extension) return false;
    
    const extLower = extension.toLowerCase();
    return ThumbnailHelper.THUMBNAIL_IMAGE_TYPES.includes(extLower);
  }

  /**
   * 获取文件类型对应的图标
   */
  static getFileIcon(fileName: string, isDir: boolean = false): string {
    if (isDir) return ThumbnailHelper.FILE_TYPE_ICONS['_folder'];
    
    const extension = MimeUtil.getExtensionWithoutDot(fileName)?.toLowerCase() || '';
    return ThumbnailHelper.FILE_TYPE_ICONS[extension] || ThumbnailHelper.FILE_TYPE_ICONS['_default'];
  }

  /**
   * 获取缩略图URL - 使用独立的缩略图接口
   * @param matter 文件对象
   * @param options 缩略图选项
   * @param successCallback 成功回调
   * @param errorCallback 错误回调
   */
  static getThumbnailUrl(
    matter: Matter,
    options: {
      width?: number,
      height?: number,
      quality?: number,
      mode?: 'cover' | 'contain' | 'fill'
    } = {},
    successCallback: (thumbnailUrl: string) => void,
    errorCallback?: (error: string) => void
  ): void {
    const {
      width = 200,
      height = 200,
      quality = 70,
      mode = 'cover'
    } = options;

    // 构建缩略图URL
    const buildThumbnailUrl = (token?: string) => {
      // 使用独立的缩略图接口
      const url = new URL('/api/matter/thumbnail', window.location.origin);
      
      // 必需参数
      url.searchParams.set('uuid', matter.uuid!);
      url.searchParams.set('width', width.toString());
      url.searchParams.set('height', height.toString());
      url.searchParams.set('quality', quality.toString());
      url.searchParams.set('mode', mode);
      
      // 可选：添加时间戳防止缓存（开发环境）
      if (process.env.NODE_ENV === 'development') {
        url.searchParams.set('_t', Date.now().toString());
      }
      
      // 如果需要token
      if (token) {
        url.searchParams.set('token', token);
      }
      
      return url.toString();
    };

    // 判断是否需要token
    const needToken = matter.privacy;
    
    if (needToken) {
      const downloadToken = new DownloadToken();
      downloadToken.httpFetchDownloadToken(
        matter.uuid!,
        () => {
          try {
            const thumbnailUrl = buildThumbnailUrl(downloadToken.uuid!);
            successCallback(thumbnailUrl);
          } catch (error) {
            if (errorCallback) {
              errorCallback(`构建缩略图URL失败: ${error instanceof Error ? error.message : '未知错误'}`);
            }
          }
        },
        () => {
          if (errorCallback) {
            errorCallback('获取下载令牌失败');
          }
        }
      );
    } else {
      try {
        const thumbnailUrl = buildThumbnailUrl();
        successCallback(thumbnailUrl);
      } catch (error) {
        if (errorCallback) {
          errorCallback(`构建缩略图URL失败: ${error instanceof Error ? error.message : '未知错误'}`);
        }
      }
    }
  }

  /**
   * 获取分享文件的缩略图URL
   * @param shareUuid 分享UUID
   * @param matter 文件对象
   * @param options 缩略图选项
   * @param successCallback 成功回调
   * @param errorCallback 错误回调
   */
  static getShareThumbnailUrl(
    shareUuid: string,
    matter: Matter,
    options: {
      width?: number,
      height?: number,
      quality?: number
    } = {},
    successCallback: (thumbnailUrl: string) => void,
    errorCallback?: (error: string) => void
  ): void {
    const { width = 200, height = 200, quality = 70 } = options;

    try {
      // 使用分享文件的缩略图接口
      const url = new URL('/api/share/matter/thumbnail', window.location.origin);
      url.searchParams.set('shareUuid', shareUuid);
      url.searchParams.set('matterUuid', matter.uuid!);
      url.searchParams.set('width', width.toString());
      url.searchParams.set('height', height.toString());
      url.searchParams.set('quality', quality.toString());
      
      // 可选：添加时间戳防止缓存
      if (process.env.NODE_ENV === 'development') {
        url.searchParams.set('_t', Date.now().toString());
      }
      
      successCallback(url.toString());
    } catch (error) {
      if (errorCallback) {
        errorCallback(`构建分享缩略图URL失败: ${error instanceof Error ? error.message : '未知错误'}`);
      }
    }
  }

  /**
   * 预加载缩略图（用于提高显示性能）
   * @param thumbnailUrl 缩略图URL
   * @returns Promise，解析为加载完成的Image对象
   */
  static preloadThumbnail(thumbnailUrl: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      // 设置超时
      const timeoutId = setTimeout(() => {
        img.src = ''; // 中断加载
        reject(new Error('缩略图加载超时'));
      }, 10000); // 10秒超时
      
      img.onload = () => {
        clearTimeout(timeoutId);
        resolve(img);
      };
      
      img.onerror = (error) => {
        clearTimeout(timeoutId);
        reject(new Error(`缩略图加载失败: ${error}`));
      };
      
      img.src = thumbnailUrl;
    });
  }

  /**
   * 批量预加载缩略图
   * @param thumbnailUrls 缩略图URL数组
   * @param concurrency 并发数，默认3
   * @param onProgress 进度回调
   */
  static async batchPreloadThumbnails(
    thumbnailUrls: string[],
    concurrency: number = 3,
    onProgress?: (loaded: number, total: number) => void
  ): Promise<void> {
    const total = thumbnailUrls.length;
    let loaded = 0;
    
    // 使用分片并发加载
    const chunks: string[][] = [];
    for (let i = 0; i < thumbnailUrls.length; i += concurrency) {
      chunks.push(thumbnailUrls.slice(i, i + concurrency));
    }
    
    for (const chunk of chunks) {
      await Promise.allSettled(
        chunk.map(url => 
          this.preloadThumbnail(url)
            .then(() => {
              loaded++;
              if (onProgress) {
                onProgress(loaded, total);
              }
            })
            .catch(error => {
              console.warn(`缩略图预加载失败: ${url}`, error);
              loaded++; // 即使失败也算加载完成
              if (onProgress) {
                onProgress(loaded, total);
              }
            })
        )
      );
    }
  }

  /**
   * 获取缩略图容器的CSS样式
   * @param thumbnailUrl 缩略图URL
   * @param isImage 是否为图片文件
   * @param size 尺寸
   * @returns CSS样式对象
   */
  static getThumbnailStyle(
    thumbnailUrl?: string,
    isImage: boolean = false,
    size: { width: number; height: number } = { width: 150, height: 150 }
  ): React.CSSProperties {
    if (isImage && thumbnailUrl) {
      return {
        width: `${size.width}px`,
        height: `${size.height}px`,
        backgroundImage: `url("${thumbnailUrl}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundColor: '#f5f5f5',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      };
    } else {
      return {
        width: `${size.width}px`,
        height: `${size.height}px`,
        backgroundColor: '#f0f2f5',
        borderRadius: '4px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: `${Math.min(size.width, size.height) * 0.4}px`,
        color: '#666'
      };
    }
  }

  /**
   * 生成图片元素的HTML字符串（用于非React环境）
   * @param matter 文件对象
   * @param size 尺寸
   * @param className CSS类名
   * @returns HTML字符串
   */
  static generateThumbnailHTML(
    matter: Matter,
    size: { width: number; height: number } = { width: 150, height: 150 },
    className: string = ''
  ): string {
    const isImage = this.isThumbnailSupported(matter.name);
    const icon = this.getFileIcon(matter.name, matter.dir);
    
    if (isImage && !matter.dir) {
      // 异步加载图片，先显示图标
      return `
        <div class="thumbnail-container ${className}" 
             style="width: ${size.width}px; height: ${size.height}px; 
                    background-color: #f5f5f5; border-radius: 4px;
                    display: flex; align-items: center; justify-content: center;
                    overflow: hidden; position: relative;">
          <div class="thumbnail-icon" style="font-size: ${size.width * 0.3}px;">
            ${icon}
          </div>
          <img class="thumbnail-image" 
               data-uuid="${matter.uuid}"
               alt="${matter.name}"
               style="display: none; width: 100%; height: 100%; object-fit: cover;"
               onload="this.style.display='block'; this.previousElementSibling.style.display='none';"
               onerror="this.style.display='none';" />
        </div>
      `;
    } else {
      return `
        <div class="thumbnail-icon ${className}" 
             style="width: ${size.width}px; height: ${size.height}px;
                    background-color: #f0f2f5; border-radius: 4px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: ${size.width * 0.4}px;">
          ${icon}
        </div>
      `;
    }
  }

  /**
   * 获取默认缩略图URL（当图片加载失败时使用）
   * @param matter 文件对象
   * @param size 尺寸
   * @returns 默认缩略图URL
   */
  static getDefaultThumbnailUrl(
    matter: Matter,
    size: { width: number; height: number } = { width: 150, height: 150 }
  ): string {
    // 这里可以返回一个默认的占位图URL
    // 例如：data URI 生成的简单图标
    const icon = this.getFileIcon(matter.name, matter.dir);
    
    // 创建一个简单的SVG作为占位图
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="${size.width}" height="${size.height}">
        <rect width="100%" height="100%" fill="#f0f2f5"/>
        <text x="50%" y="50%" font-family="Arial" font-size="${size.width * 0.3}" 
              text-anchor="middle" dy=".3em" fill="#666">
          ${icon}
        </text>
      </svg>
    `;
    
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  }

  /**
   * 格式化文件大小
   * @param bytes 字节数
   * @returns 格式化后的字符串
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * 获取文件扩展名（不带点）
   * @param fileName 文件名
   * @returns 扩展名
   */
  static getFileExtension(fileName: string): string {
    return MimeUtil.getExtensionWithoutDot(fileName)?.toLowerCase() || '';
  }

  /**
   * 检查是否为常见图片格式
   * @param extension 文件扩展名
   * @returns 是否为图片
   */
  static isImageExtension(extension: string): boolean {
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
    return imageExtensions.includes(extension.toLowerCase());
  }

  /**
   * 检查是否为常见文档格式
   * @param extension 文件扩展名
   * @returns 是否为文档
   */
  static isDocumentExtension(extension: string): boolean {
    const documentExtensions = ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx', 'txt', 'rtf'];
    return documentExtensions.includes(extension.toLowerCase());
  }

  /**
   * 检查是否为常见视频格式
   * @param extension 文件扩展名
   * @returns 是否为视频
   */
  static isVideoExtension(extension: string): boolean {
    const videoExtensions = ['mp4', 'avi', 'mov', 'mkv', 'flv', 'wmv', 'webm'];
    return videoExtensions.includes(extension.toLowerCase());
  }

  /**
   * 检查是否为常见音频格式
   * @param extension 文件扩展名
   * @returns 是否为音频
   */
  static isAudioExtension(extension: string): boolean {
    const audioExtensions = ['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a'];
    return audioExtensions.includes(extension.toLowerCase());
  }
}

// 导出常量以便其他地方使用（现在可以通过 ThumbnailHelper.THUMBNAIL_IMAGE_TYPES 访问）
// 这些导出只是为了方便，可以直接从类访问
export const THUMBNAIL_IMAGE_TYPES = ThumbnailHelper.THUMBNAIL_IMAGE_TYPES;
export const FILE_TYPE_ICONS = ThumbnailHelper.FILE_TYPE_ICONS;